import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { Reveal } from "./shared";
import { CONTACT_INFO } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full border border-white/20 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:border-vec-green-bright focus:outline-none transition-colors duration-300";

const labelCls = "block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2";

export const Contact = () => {
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", requirement: "", message: "" });
    const [sending, setSending] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("Enquiry sent. The VEC team will get back to you.");
            setForm({ name: "", company: "", email: "", phone: "", requirement: "", message: "" });
        } catch (err) {
            toast.error(err.response?.data?.detail?.[0]?.msg || "Could not send your enquiry. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative bg-vec-charcoal text-white py-16 lg:py-32 overflow-hidden">
            <div className="absolute inset-0 tech-grid-dark opacity-40" aria-hidden="true" />
            <div className="relative vec-container">
                <div className="grid grid-cols-12 gap-y-10 lg:gap-x-16">
                    <div className="col-span-12 lg:col-span-5">
                        <Reveal>
                            <div className="flex items-center gap-3 font-mono-tech text-[11px] sm:text-xs uppercase tracking-[0.22em] text-vec-green-bright">
                                <span className="text-white/40">SEC 12</span>
                                <span className="h-px w-10 bg-vec-green-bright/60" aria-hidden="true" />
                                <span>Contact</span>
                            </div>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="mt-5 font-display text-[clamp(1.9rem,1.3rem+2vw,3.4rem)] font-semibold tracking-tight leading-[1.08] uppercase text-balance">
                                Let's Build What Powers Tomorrow.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/65 max-w-md">
                                Looking for a project partner? Tell us what you're building.
                            </p>
                        </Reveal>
                        <Reveal delay={0.24}>
                            <ul className="mt-10 space-y-5 border-t border-white/10 pt-8">
                                {[
                                    { icon: Phone, label: "Phone", value: CONTACT_INFO.phone, testId: "contact-phone" },
                                    { icon: Mail, label: "Email", value: CONTACT_INFO.email, testId: "contact-email" },
                                    { icon: MapPin, label: "Address", value: CONTACT_INFO.address, testId: "contact-address" },
                                ].map((row) => (
                                    <li key={row.label} className="flex items-start gap-4">
                                        <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center border border-white/15 text-vec-green-bright">
                                            <row.icon size={15} aria-hidden="true" />
                                        </span>
                                        <span>
                                            <span className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/45">
                                                {row.label}
                                            </span>
                                            <span data-testid={row.testId} className="mt-1 block text-sm text-white/75">
                                                {row.value}
                                            </span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>

                    <div className="col-span-12 lg:col-span-7">
                        <Reveal delay={0.12}>
                            <form
                                data-testid="contact-enquiry-form"
                                onSubmit={submit}
                                className="border border-white/15 bg-white/[0.03] p-6 sm:p-9 grid grid-cols-1 sm:grid-cols-2 gap-5"
                            >
                                <div>
                                    <label htmlFor="enquiry-name" className={labelCls}>Name *</label>
                                    <input id="enquiry-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
                                </div>
                                <div>
                                    <label htmlFor="enquiry-company" className={labelCls}>Company</label>
                                    <input id="enquiry-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder="Organisation name" />
                                </div>
                                <div>
                                    <label htmlFor="enquiry-email" className={labelCls}>Email *</label>
                                    <input id="enquiry-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
                                </div>
                                <div>
                                    <label htmlFor="enquiry-phone" className={labelCls}>Phone</label>
                                    <input id="enquiry-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="enquiry-requirement" className={labelCls}>Project / Requirement</label>
                                    <input id="enquiry-requirement" data-testid="contact-requirement-input" value={form.requirement} onChange={set("requirement")} className={inputCls} placeholder="e.g. Substation O&M, tower erection, solar infrastructure" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="enquiry-message" className={labelCls}>Message *</label>
                                    <textarea id="enquiry-message" data-testid="contact-message-input" required rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your project or requirement" />
                                </div>
                                <div className="sm:col-span-2 flex flex-wrap items-center gap-5">
                                    <button
                                        type="submit"
                                        data-testid="contact-submit-button"
                                        disabled={sending}
                                        className="group inline-flex items-center gap-3 bg-vec-green hover:bg-vec-green-bright disabled:opacity-60 text-white font-semibold text-sm tracking-wide px-8 py-4 transition-colors duration-300"
                                    >
                                        {sending ? "Sending…" : "Send Enquiry"}
                                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                                    </button>
                                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/35">
                                        Response from the VEC team
                                    </span>
                                </div>
                            </form>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
